/**
 * Editor de atributos do produto (#214/#215): dietas, picância, tags de
 * sabor, infantil e prato da casa. Sugestões da IA chegam pré-marcadas (✦);
 * só "Confirmar atributos" as transforma em facto — mesmo contrato do
 * AllergensEditor (#325).
 */
import { useState } from 'react'
import { useMutation, type useQueryClient } from '@tanstack/react-query'
import { products as prodApi, type Product } from '../lib/api.js'
import {
  attributeState,
  attributesChanged,
  initialAttributes,
  ATTRIBUTE_BADGE,
} from '../lib/attributes.js'
import { Button, toast, Badge } from './ui/index.js'
import { AttributeFields, type AttributeSelection } from './AttributeFields.js'
import { useI18n } from '../lib/i18n.js'

export function AttributesEditor({
  product,
  qc,
  onProductUpdate,
  /** sem o botão de sugestão IA (ex.: revisão #216 já traz as sugestões) */
  withSuggest = true,
}: {
  product: Product
  qc: ReturnType<typeof useQueryClient>
  onProductUpdate: (p: Product) => void
  withSuggest?: boolean
}) {
  const { t } = useI18n()
  const [selection, setSelection] = useState<AttributeSelection>(() => initialAttributes(product))
  // produto trocado ou confirmado noutro lado: recomeça da lista do servidor
  const stamp = `${product.id}|${product.attributes?.confirmed_at ?? ''}|${product.attributes?.status ?? ''}`
  const [prevStamp, setPrevStamp] = useState(stamp)
  if (stamp !== prevStamp) {
    setPrevStamp(stamp)
    setSelection(initialAttributes(product))
  }

  const state = attributeState(product)
  const { color, icon } = ATTRIBUTE_BADGE[state]

  const confirmMut = useMutation({
    mutationFn: () => prodApi.confirmAttributes(product.id, selection),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      toast.success(t('admin.menu.attributes_saved'))
      if (data) onProductUpdate(data)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // #215: pede sugestões da IA para o produto avulso (status 'suggested')
  const suggestMut = useMutation({
    mutationFn: () => prodApi.suggestAttributes(product.id),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      if (data) onProductUpdate(data)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div
      className="rounded-xl border border-gray-200 p-4"
      data-testid={`catalog-attributes-${product.id}`}
      role="group"
      aria-labelledby={`catalog-attributes-${product.id}-label`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <p
          id={`catalog-attributes-${product.id}-label`}
          className="text-sm font-medium text-gray-700"
        >
          {t('admin.menu.attributes')}
        </p>
        <span data-testid={`catalog-attributes-state-${product.id}`} data-state={state}>
          <Badge color={color} className="text-xs">
            <span aria-hidden="true" className="font-bold">
              {icon}
            </span>
            <span>{t(ATTRIBUTE_BADGE[state].labelKey)}</span>
          </Badge>
        </span>
      </div>
      <p className="text-xs text-gray-400 mb-3">{t('admin.menu.attributes_hint')}</p>
      {state === 'pending' && (
        <p className="text-[11px] text-amber-700 mb-3">✦ {t('admin.menu.attributes_ai_hint')}</p>
      )}
      <AttributeFields
        selection={selection}
        onChange={setSelection}
        testIdPrefix={`catalog-attr-${product.id}-`}
      />
      <div className="flex items-center justify-end gap-2 mt-3">
        {withSuggest && (
          <Button
            size="sm"
            variant="secondary"
            data-testid={`catalog-attributes-suggest-${product.id}`}
            loading={suggestMut.isPending}
            disabled={suggestMut.isPending || confirmMut.isPending}
            onClick={() => suggestMut.mutate()}
            title={t('admin.menu.attributes_ai_hint')}
          >
            ✦{' '}
            {suggestMut.isPending
              ? t('admin.menu.attributes_suggesting')
              : t('admin.menu.attributes_suggest')}
          </Button>
        )}
        <Button
          size="sm"
          data-testid={`catalog-attributes-confirm-${product.id}`}
          loading={confirmMut.isPending}
          disabled={
            confirmMut.isPending || suggestMut.isPending || !attributesChanged(product, selection)
          }
          onClick={() => confirmMut.mutate()}
        >
          {t('admin.menu.attributes_confirm')}
        </Button>
      </div>
    </div>
  )
}
